<template>
  <div class="hosts-dashboard-content">
    <el-row :gutter="20">
      <el-col :span="24">
        <el-row>
          <el-col :span="4">
            <div class="scroll-menu">
              <h5>Details</h5>
              <el-link :underline="false" @click="scrollToElementID('details', 'host-details')">Host</el-link>
              <el-link :underline="false" @click="scrollToElementID('details', 'host-agent')">Agent</el-link>
              <el-link :underline="false" @click="scrollToElementID('details', 'host-network')">Network</el-link>
              <el-link
                  :underline="false"
                  @click="scrollToElementID('details', 'host-configuration')"
                  v-if="agent && agent.system.ec2Instance"
              >
                Cloud Configuration
              </el-link>
            </div>
            &nbsp;
          </el-col>
          <el-col :span="20">
            <el-collapse v-model="activeDetailsCollapse">
              <slot v-if="agent">
                <el-collapse-item id="host-details" title="Host" name="host-details">
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Criticality</el-col>
                    <el-col :span="18" class="content"> {{ agent.criticality }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> OS</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.os }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Kernel</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.kernel }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Release</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.release }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Version</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.version }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Platform</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.platform }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> CPUs</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.cpus }}</el-col>
                  </el-row>
                </el-collapse-item>
                <el-collapse-item id="host-agent" title="Agent" name="host-agent">
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Status</el-col>
                    <el-col :span="18" class="content">
                      <el-tag v-if="agent.agentStatus === 'ONLINE'" size="mini" type="success">ONLINE</el-tag>
                      <el-tag v-if="agent.agentStatus !== 'ONLINE'" size="mini" type="danger">OFFLINE</el-tag>
                    </el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Name</el-col>
                    <el-col :span="18" class="content"> {{ agent.agentId }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Created</el-col>
                    <el-col :span="18" class="content"> {{ formatDateTime(agent.createTime) }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Last Status Change</el-col>
                    <el-col :span="18" class="content">
                      {{ formatDateTimeNS(agent.lastStatusChangeTime) }}
                    </el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Events Consumed</el-col>
                    <el-col :span="18" class="content"> {{ agent.eventsConsumed }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Aggregated Risk Score</el-col>
                    <el-col :span="18" class="content"> {{ agent.aggregatedRiskScore }}</el-col>
                  </el-row>
                </el-collapse-item>
                <el-collapse-item id="host-network" title="Network" name="host-network">
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Hostname</el-col>
                    <el-col :span="18" class="content"> {{ agent.customHostname }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Private IPs</el-col>
                    <el-col :span="18" class="content">
                      <div v-for="ip in agent.privateIPs" :key="ip">{{ ip }}</div>
                    </el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Public IPs</el-col>
                    <el-col :span="18" class="content">
                      <div v-for="ip in agent.publicIPs" :key="ip">{{ ip }}</div>
                    </el-col>
                  </el-row>
                </el-collapse-item>
                <el-collapse-item
                    v-if="agent.system.ec2Instance"
                    id="host-configuration"
                    title="Cloud Configuration"
                    name="host-configuration"
                >
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> AMI ID</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.amiId }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Availability Zone</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.availabilityZone }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Instance ID</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.instanceId }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Instance Type</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.instanceType }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Local Hostname</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.localHostname }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Local IPv4</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.localIpv4 }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Public Hostname</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.publicHostname }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Public IPv4</el-col>
                    <el-col :span="18" class="content"> {{ agent.system.ec2Instance.publicIpv4 }}</el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="6" class="name"> Security Groups</el-col>
                    <el-col :span="18" class="content">
                      {{ agent.system.ec2Instance.securityGroups.split(/\n/).join(', ') }}
                    </el-col>
                  </el-row>
                </el-collapse-item>
              </slot>
            </el-collapse>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import moment from 'moment-timezone'
import PageHeaders from '@/components/layout/PageHeaders.vue'
import Stats from '@/views/hosts/Stats.vue'

import {
  DATETIME_FORMAT_LONG,
  NANOSECONDS_TO_MILLISECONDS,
  INSIGHTS_DATE_RANGE_CHOICES
} from '@/constants/datetime.constants'

import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'

import {AGENT_DETAILS_REQ} from '@/store/modules/agents/agents.type'
import {HOST_INSIGHTS_DATE_RANGE_PREFERENCE} from '@/store/modules/user_pref/user-pref.type'

export default {
  name: 'HostDetails',
  data() {
    return {
      daterangeUserPrefConstant: HOST_INSIGHTS_DATE_RANGE_PREFERENCE,
      dateRangeOptions: INSIGHTS_DATE_RANGE_CHOICES,
      agendId: '',
      customHostname: '',
      breadcrumbs: [{title: 'Dashboard'}, {title: 'Hosts', to: '/assets/hosts'}],
      agent: null,
      warning: false,
      activeDetectionsCollapse: [],
      activeDetailsCollapse: ['host-details', 'host-agent', 'host-network', 'host-configuration'],
      activeInsightsCollapse: [],
      activeContainersCollapse: [],
      paneHeightStyle: {
        height: '500px'
      }
    }
  },
  created() {
    this.resize()
    this.agentId = this.$route.params.agentId

    // always load tab details
    this.loadTabDetails()

    // add resize listener to window
    window.addEventListener('resize', (this.resizeListener = this.resize.bind(this)))
  },
  mounted() {
    this.loadView(this.activeTab)
  },
  beforeDestroy() {
    // remove resize listener from window
    window.removeEventListener('resize', this.resizeListener)
  },
  methods: {
    resize: _.debounce(() => {
      let OFFSET_TOP_DISTANCE = 172
      let tabPaneHeight = window.innerHeight - OFFSET_TOP_DISTANCE

      this.paneHeightStyle.height = tabPaneHeight + 'px'
    }, 100),
    switchView(tab, event) {
      this.$router.replace({
        query: {
          view: tab.paneName
        }
      })
      this.loadView(tab.paneName)
    },
    loadView(name) {
      switch (name) {
        case 'details':
          this.loadTabDetails()
          break
        case 'insights':
          this.loadTabInsights()
          break
      }
    },
    loadTabDetails() {
      this.$store.dispatch(AGENT_DETAILS_REQ, this.agentId).then(
          agent => {
            this.agent = agent
            this.agent.publicIPs = this.agent.publicIPs.sort()
            this.agent.privateIPs = this.agent.privateIPs.sort()
            this.customHostname = this.agent.customHostname
            if (this.breadcrumbs.length === 2) {
              this.breadcrumbs.push({title: this.customHostname})
            }
            if (this.agent.aggregatedRiskScore > 0) {
              this.warning = 'at risk'
            }
            if (this.agent.containers.length > 0) {
              this.agent.containers.forEach(container => {
                this.activeContainersCollapse.push(container.containerId)
              })
            }
          },
          errMsg => {
            const h = this.$createElement
            this.$msgbox({
              title: 'Details Error',
              message: h('p', null, 'There was an error loading the agent you requested.'),
              showCancelButton: false,
              confirmButtonText: 'Ok',
              beforeClose: (action, instance, done) => {
                window.location.href = '/#/hosts'
              }
            })
          }
      )
    },
    loadTabInsights() {
      this.$refs.dns.fetchHostInsights()
      this.$refs.ips.fetchHostInsights()
      this.$refs.logins.fetchHostInsights()
      this.$refs.powershell.fetchHostInsights()
    },
    insightsUpdated(update) {
      let name = update.name
      let actives = this.activeInsightsCollapse
      if (update.hasChartData) {
        actives.push(name)
      }
    },
    formatDateTime(datetime) {
      return moment(datetime).format(DATETIME_FORMAT_LONG)
    },
    formatDateTimeNS(datetime) {
      return this.formatDateTime(datetime * NANOSECONDS_TO_MILLISECONDS)
    },
    scrollToElementID(pane, id) {
      let OFFSET_TOP_DISTANCE = 169

      let val
      let tabs = this.$el
      let element = document.getElementById(id)
      let rect = element.getBoundingClientRect()
      let to = rect.y - OFFSET_TOP_DISTANCE + tabs.scrollTop

      let start = tabs.scrollTop
      let change = to - start
      let currentTime = 0
      let increment = 20
      let duration = 250
      let easeInOutQuad = (t, b, c, d) => {
        t /= d / 2
        if (t < 1) return (c / 2) * t * t + b
        t--
        return (-c / 2) * (t * (t - 2) - 1) + b
      }
      let animateScroll = () => {
        currentTime += increment
        val = easeInOutQuad(currentTime, start, change, duration)
        tabs.scrollTop = val
        if (currentTime < duration) {
          setTimeout(animateScroll, increment)
        }
      }
      animateScroll()
    }
  },
  computed: {
    ...mapGetters([
      'insightsEnabled',
      'agentDetailsReqStatus',
      'hostInsightsDateRangeUserSetting',
      'hrlyDnsRequestStatisticsReqStatus',
      'hrlyExternalIpStatisticsReqStatus',
      'hrlyLoginFailuresStatisticsReqStatus',
      'hrlyPrivilegedExecStatisticsReqStatus'
    ]),
    activeTab: {
      get: function () {
        return this.$route.query.view || 'details'
      },
      set: function (val) {
      }
    }
  },
  watch: {
    agentDetailsReqStatus: {
      handler: (newStatus, oldStatus) => {
        if (newStatus === 'error') {
          console.log('error')
        }
      }
    },
    hostInsightsDateRangeUserSetting: {
      handler: function (newDateRange, oldDateRange) {
        if (newDateRange !== oldDateRange) {
          this.loadView(this.activeTab)
        }
      }
    }
  },
  components: {
    PageHeaders,
    Stats,
    DateRangeDropdown
  }
}
</script>

<style scoped>
.hosts-dashboard-content {
  overflow: scroll;
  height: calc(100% - 145px);
}

.scroll-menu {
  position: fixed;
}

.scroll-menu h5 {
  margin-top: 0.5em;
  margin-bottom: 1.5em;
}

.scroll-menu a {
  display: block;
  margin: 0.5em 0;
}

>>> .el-collapse-item__header {
  height: 36px;
  padding-left: 15px;
  font-weight: 700;
  background: #f4f4f4;
}

>>> .el-collapse-item__content {
  padding: 15px;

  .name {
    color: #999999;
    font-size: 12px;
    font-weight: bold;
  }

  .el-row {
    margin-bottom: 5px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/hosts/HostDetails.vue